/**
 * Sending-protection presentation logic (Node B3) — the pure mapping from an
 * escalation state to the copy + styling the customer-facing UI shows. Kept free
 * of React/CSS so it is the single, unit-tested source of truth for the persistent
 * global banner, the Settings nav dot, and the in-app status/recovery pages.
 *
 * Copy is ported verbatim from the LOCKED prototypes
 * (notes/graphics-prototypes/flashroom-sending-journey.html + -recovery.html);
 * the live spam rate + pause line are interpolated where the prototype shows a
 * placeholder number so the message reflects the workspace's real reputation.
 */

/** The escalation states, in severity order (mirrors the backend `SENDING_STATES`). */
export type SendingStateId =
  | "healthy"
  | "headsup"
  | "paused"
  | "recovering"
  | "final"
  | "stopped";

/** The spam-complaint pause line (0.3%) — the constant the copy references. */
export const SPAM_PAUSE_LINE = 0.003;

/** A fraction (0.0024) as a trimmed percentage label ("0.24"), no trailing zeros. */
export function ratePct(rate: number): string {
  return (Math.round(rate * 100 * 100) / 100).toString();
}

/** The banner tone → maps to a CSS variant (brass = amber, bad = red, ink = dark). */
export type BannerTone = "brass" | "bad" | "ink";
/** The banner glyph. */
export type BannerIcon = "warn" | "pause" | "stop";

interface SendingBannerContent {
  tone: BannerTone;
  icon: BannerIcon;
  title: string;
  body: string;
  /** The action-button label — always routes to the in-app status page. */
  cta: string;
}

/**
 * The persistent banner for a state, or `null` when healthy (no banner shown).
 * `spamRate` is the workspace's live complaint rate (a fraction), interpolated
 * into the states whose prototype copy quotes it.
 */
export function sendingBanner(state: string, spamRate: number): SendingBannerContent | null {
  const spam = ratePct(spamRate);
  const line = ratePct(SPAM_PAUSE_LINE);
  switch (state) {
    case "headsup":
      return {
        tone: "brass",
        icon: "warn",
        title: "Heads up: your spam complaints are climbing",
        body: `You’re at ${spam}%. Passing ${line}% pauses your sending.`,
        cta: "See details",
      };
    case "paused":
      return {
        tone: "bad",
        icon: "pause",
        title: "Your email sending is paused",
        body: `Spam hit ${spam}% (limit ${line}%). Your reminders and follow-ups are on hold.`,
        cta: "See how to fix it",
      };
    case "recovering":
      return {
        tone: "brass",
        icon: "warn",
        title: "Coming back: we’re watching your next sends",
        body: "Sending at a reduced limit. Stay clean and full sending returns on its own.",
        cta: "View progress",
      };
    case "final":
      return {
        tone: "bad",
        icon: "pause",
        title: "Paused again: this is your final chance",
        body: "One more spam problem and your sending is turned off.",
        cta: "See how to fix it",
      };
    case "stopped":
      return {
        tone: "ink",
        icon: "stop",
        title: "Your sending is turned off",
        body: "After repeated spam problems, sending is off.",
        cta: "See details",
      };
    default:
      return null;
  }
}

interface SendingStatusMeta {
  /** The status-pill tone (brass = amber, bad = red, ink = dark). */
  pill: BannerTone;
  pillLabel: string;
  heading: string;
}

/** The status/recovery page's pill + heading per non-healthy state. */
export const SENDING_STATUS_META: Record<Exclude<SendingStateId, "healthy">, SendingStatusMeta> = {
  headsup: { pill: "brass", pillLabel: "Getting close", heading: "You’re getting close to the line" },
  paused: { pill: "bad", pillLabel: "Sending paused", heading: "Your sending is paused" },
  recovering: { pill: "brass", pillLabel: "Coming back, watched", heading: "You’re sending again" },
  final: { pill: "bad", pillLabel: "Final chance", heading: "Paused again: final chance" },
  stopped: { pill: "ink", pillLabel: "Sending off", heading: "Your sending is turned off" },
};

/** The Settings nav-dot colour: amber while warned, red while paused-or-worse, none when healthy. */
export function sendingNavDot(state: string): "warned" | "paused" | null {
  if (state === "headsup" || state === "recovering") return "warned";
  if (state === "paused" || state === "final" || state === "stopped") return "paused";
  return null;
}

/** True when the state should be surfaced (anything but healthy). */
export function isSendingAlerting(state: string): boolean {
  return state !== "healthy" && sendingBanner(state, 0) !== null;
}

/** Why a workspace's emails cannot go out right now, or null when they can (or it is not known yet). */
export type HeldReason = "no_domain" | "pending" | "paused";

/** The states FlashEmail refuses to send in; the rest still send. */
const REFUSING = new Set(["paused", "final", "stopped"]);

export function heldReason(
  status: { domain_status?: string; state?: string } | null | undefined,
): HeldReason | null {
  if (!status?.domain_status) return null;
  if (status.domain_status === "off") return "no_domain";
  if (status.domain_status === "pending") return "pending";
  if (status.state && REFUSING.has(status.state)) return "paused";
  return null;
}

/** The line under a step whose email cannot go out, by what the step is. */
export function heldLine(reason: HeldReason, kind: "room" | "email" | "notify"): string {
  const why = reason === "paused" ? "paused with the rest of your sending" : reason === "pending" ? "held back until your domain is verified" : "held back until sending is set up";
  if (kind === "room") return `Lands on their page. Its email is ${why}.`;
  if (kind === "notify") return `Your team's email is ${why}.`;
  return `This email is ${why}.`;
}
